// ─────────────────────────────────────────────────────────────
// Single source of truth for the languages the app supports.
//
// To ADD or REMOVE a language, edit this array. Then add the matching
// static locale imports (bundlers require literal import paths, so these
// two can't be generated from the array):
//   • dayjs: `import 'dayjs/locale/<dayjs>'` in src/shared/date/date.config.ts
//   • antd:  `import x from 'antd/es/locale/<antd>'` + the ANTD_LOCALE_BY_CODE
//            entry in src/refactory/utils/antd.ts (TS will flag a missing one).
// ─────────────────────────────────────────────────────────────
export const LANGUAGES = [
  { code: 'en', label: 'English', dayjs: 'en', antd: 'en_US' },
  { code: 'es', label: 'Español', dayjs: 'es', antd: 'es_ES' },
  { code: 'fr', label: 'Français', dayjs: 'fr', antd: 'fr_FR' },
  { code: 'it', label: 'Italiano', dayjs: 'it', antd: 'it_IT' },
  { code: 'nl', label: 'Nederlands', dayjs: 'nl', antd: 'nl_NL' },
  { code: 'pt', label: 'Português', dayjs: 'pt', antd: 'pt_PT' },
  { code: 'pt-br', label: 'Português (BR)', dayjs: 'pt-br', antd: 'pt_BR' },
  { code: 'uk', label: 'Українська', dayjs: 'uk', antd: 'uk_UA' }
] as const

export type LanguageCode = typeof LANGUAGES[number]['code']

export const SUPPORTED_LANGUAGE_CODES: LanguageCode[] = LANGUAGES.map((l) => l.code)
export const DEFAULT_LANGUAGE: LanguageCode = 'en'

// Normalize any i18n / browser language ('ES', 'pt-BR', 'en-US', …) to a
// supported code, falling back to the default. Used to keep dayjs and antd
// locales in sync with i18n from one place.
export const toLanguageCode = (lng?: string): LanguageCode => {
  if (!lng) return DEFAULT_LANGUAGE
  const lower = lng.toLowerCase()
  if (SUPPORTED_LANGUAGE_CODES.includes(lower as LanguageCode)) return lower as LanguageCode
  const base = lower.split('-')[0] as LanguageCode
  return SUPPORTED_LANGUAGE_CODES.includes(base) ? base : DEFAULT_LANGUAGE
}
